import styled from 'styled-components';
import { Tabs, Card, Row, Table } from 'antd';
import { COLORS } from '../../constants/colors';

export const TabsAndFiltersRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 24px;
  border-bottom: 1px solid ${COLORS.border};
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const FiltersContainer = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
  flex-shrink: 0;

  /* Override global 48px to 40px for all controls inside filters */
  .ant-btn,
  .ant-btn-default,
  .ant-btn-primary {
    height: 40px !important;
    min-height: 40px !important;
    max-height: 40px !important;
    font-size: 14px !important;
    padding: 0 16px !important;
  }

  .ant-select,
  .ant-select-single,
  .ant-select-lg,
  .ant-select-single.ant-select-lg {
    height: 40px !important;
  }

  .ant-select .ant-select-selector,
  .ant-select-lg .ant-select-selector,
  .ant-select-single .ant-select-selector,
  .ant-select-single.ant-select-lg .ant-select-selector {
    height: 40px !important;
    min-height: 40px !important;
    font-size: 14px !important;
  }

  .ant-select .ant-select-selection-placeholder,
  .ant-select .ant-select-selection-item,
  .ant-select-lg .ant-select-selection-placeholder,
  .ant-select-lg .ant-select-selection-item {
    line-height: 38px !important;
    font-size: 14px !important;
  }

  .ant-picker,
  .ant-picker-large {
    height: 40px !important;
    min-height: 40px !important;
    font-size: 14px !important;
  }

  .ant-picker .ant-picker-input input,
  .ant-picker-large .ant-picker-input input {
    font-size: 14px !important;
  }

  .ant-input,
  .ant-input-lg {
    height: 40px !important;
    min-height: 40px !important;
    font-size: 14px !important;
  }
`;

/* Shared Analytics Card Styles */
export const AnalyticsCard = styled(Card)`
  border-radius: 12px;
  border: 1px solid ${COLORS.border};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  height: 100%;
  transition: box-shadow 0.2s ease;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
  
  .ant-card-head {
    border-bottom: 1px solid ${COLORS.border};
    padding: 16px 20px;
    min-height: auto;
    
    .ant-card-head-title {
      font-size: 15px;
      font-weight: 600;
      color: ${COLORS.textDark};
      padding: 0;
    }
  }
  
  .ant-card-body {
    padding: 20px;
  }
`;

export const ChartCard = styled(AnalyticsCard)`
  margin-bottom: 24px;
  
  .ant-card-body {
    padding: 24px;
  }
`;

export const StatCard = styled(AnalyticsCard)`
  .ant-card-body {
    padding: 20px;
  }
  
  .ant-statistic-title {
    font-size: 13px;
    color: ${COLORS.textMedium};
    margin-bottom: 8px;
  }
  
  .ant-statistic-content {
    font-size: 28px;
    font-weight: 600;
  }
`;

export const StatsRow = styled(Row)`
  margin-bottom: 24px;
`;

export const ChartsRow = styled(Row)`
  margin-bottom: 24px;
`;

export const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${COLORS.textDark};
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const StyledAnalyticsTabs = styled(Tabs)`
  margin: 0 !important;
  background: transparent;
  padding-top: 0;
  padding-bottom: 8px;
  margin-bottom: 0;
  
  .ant-tabs-nav {
    margin: 0 !important;
    
    &::before {
      display: none !important;
    }
  }
  
  /* Hide ink bar */
  .ant-tabs-ink-bar {
    display: none !important;
  }
  
  /* Make the tab strip horizontally scrollable on small widths */
  .ant-tabs-nav-wrap {
    overflow-x: auto;
    scrollbar-width: none; /* Firefox */
    
    &::-webkit-scrollbar {
      display: none; /* WebKit */
    }
  }
  
  .ant-tabs-nav-list {
    display: flex;
    gap: 4px;
    align-items: center;
    justify-content: flex-start;
    width: auto;
    
    .ant-tabs-tab {
      position: relative;
      padding: 0;
      margin: 0;
      background: transparent;
      border: none;
      transition: all 0.2s ease;
      flex: none;
      width: auto;
      
      /* Remove dividers */
      &::before {
        display: none;
      }
      
      .ant-tabs-tab-btn {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 10px 14px;
        border-radius: 999px;
        color: ${COLORS.textMedium};
        font-weight: 500;
        font-size: 14px;
        transition: background-color .2s ease, color .2s ease;
        background: transparent;
        border: none;
        min-height: 40px;
        white-space: nowrap;
        
        .anticon {
          font-size: 16px;
        }
      }
      
      &:hover .ant-tabs-tab-btn {
        color: ${COLORS.textDark};
        background: rgba(24, 144, 255, 0.08);
      }
      
      &.ant-tabs-tab-active .ant-tabs-tab-btn {
        color: ${COLORS.$primaryBlue};
        background: #eef4ff;
        font-weight: 600;
        box-shadow: 0 1px 3px rgba(0,0,0,.06);
        border: 1px solid rgba(24, 144, 255, 0.3);
      }
      
      /* Visible keyboard focus */
      .ant-tabs-tab-btn:focus-visible {
        outline: 2px solid ${COLORS.$primaryBlue};
        outline-offset: 2px;
        border-radius: 999px;
      }
    }
  }
  
  /* Show content holder for analytics sub-tabs */
  .ant-tabs-content-holder {
    display: block;
  }
  
  /* Responsive adjustments */
  @media (max-width: 768px) {
    .ant-tabs-nav-list {
      gap: 2px;
      
      .ant-tabs-tab {
        .ant-tabs-tab-btn {
          font-size: 13px;
          padding: 8px 12px;
          min-height: 36px;
          
          .anticon {
            font-size: 14px;
          }
        }
      }
    }
  }
  
  @media (max-width: 576px) {
    .ant-tabs-nav-list {
      .ant-tabs-tab {
        .ant-tabs-tab-btn {
          font-size: 12px;
          padding: 6px 8px;
          min-height: 32px;
          gap: 4px;
          
          .anticon {
            font-size: 12px;
          }
        }
      }
    }
  }
`;

export const TabsBorderDiv = styled.div`
  border-bottom: 1px solid ${COLORS.border};
  margin-bottom: 20px;
`;

// Shared PageContainer with animation keyframes - used across all Analytics pages
export const PageContainer = styled.div`
  /* Constrain width on very large screens so dashboards don't stretch oddly */
  width: 100%;
  max-width: 1600px;
  margin-left: auto;
  margin-right: auto;

  /* Animation Keyframes */
  @keyframes fadeInDown {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes fadeInScale {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  @keyframes slideInLeft {
    from {
      opacity: 0;
      transform: translateX(-20px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  /* Animation Classes */
  .header-animate-in {
    animation: fadeInDown 0.5s ease-out forwards;
  }

  .filters-animate-in {
    animation: slideInLeft 0.5s ease-out forwards;
    animation-delay: 0.1s;
    opacity: 0;
  }

  .stats-animate-in {
    animation: fadeInUp 0.5s ease-out forwards;
    animation-delay: 0.2s;
    opacity: 0;
  }

  .content-animate-in {
    animation: fadeInScale 0.5s ease-out forwards;
    animation-delay: 0.3s;
    opacity: 0;
  }

  .chart-animate-in {
    animation: fadeInScale 0.5s ease-out forwards;
    animation-delay: 0.25s;
    opacity: 0;
  }
`;

// Common inline style objects for reuse
export const selectStyles = {
  width: 170
};

export const rangePickerStyles = {
  minWidth: 200
};

export const exportButtonStyles = {
  height: 32
};

export const eventSelectStyles = {
  width: 200
};

// Statistic value colors
export const valueColors = {
  success: '#108043',
  successAlt: '#3f8600',
  warning: '#faad14',
  danger: '#cf1322',
  dangerAlt: '#b91c1c',
  info: '#1890ff',
  purple: '#722ed1',
  cyan: '#13c2c2'
};

// Statistic valueStyle objects
export const valueStyles = {
  successAlt: { color: '#3f8600' },
  info: { color: '#1890ff' },
  danger: { color: '#cf1322' },
  purple: { color: '#722ed1' }
};

// Statistic suffix styles
export const suffixStyles = {
  fontSize: 14
};

// Icon prefix colors (as style objects for Statistic prefix)
export const iconColors = {
  primary: { color: COLORS.$primaryBlue },
  success: { color: '#108043' },
  warning: { color: '#faad14' },
  danger: { color: '#b91c1c' },
  purple: { color: '#722ed1' },
  cyan: { color: '#13c2c2' }
};

// Table cell styles
export const tableCellStyles = {
  title: { fontWeight: 500 },
  subtitle: { fontSize: 12, color: '#8c8c8c' }
};

// Margin style objects
export const marginStyles = {
  mb16: { marginBottom: 16 },
  mb24: { marginBottom: 24 },
  mt16: { marginTop: 16 },
  mt24: { marginTop: 24 }
};

// Drawer table styled like the members table
export const DrawerTable = styled(Table)`
  background: white;
  border-radius: 12px;
  border: 1px solid #f0f0f0;
  overflow: hidden;

  .ant-table {
    border-radius: 0;
    background: white;
    border: none;
  }

  .ant-table-thead > tr > th {
    background: white !important;
    color: ${COLORS.textDark || '#1a1a2e'} !important;
    border-bottom: 3px solid ${COLORS.$primaryBlue || '#1890ff'} !important;
    font-weight: 600 !important;
    font-size: 12px !important;
    height: 44px !important;
    padding: 10px 12px !important;
    text-transform: uppercase !important;
    letter-spacing: 0.5px !important;
  }

  .ant-table-tbody > tr {
    transition: all 0.2s ease;
    border-bottom: 1px solid #f5f5f5;

    &:hover > td {
      background: #fafbff !important;
    }

    &:nth-child(even) {
      background: #fafafa;
      &:hover > td {
        background: #f0f7ff !important;
      }
    }
  }

  .ant-table-tbody > tr > td {
    border-bottom: none;
    padding: 10px 12px;
    font-size: 13px;
    line-height: 1.5;
    vertical-align: middle;
  }

  .ant-pagination {
    margin: 0 !important;
    padding: 12px 16px !important;
    border-top: 1px solid ${COLORS.border || '#e8e8e8'};
    display: flex;
    justify-content: flex-end;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;

    .ant-pagination-total-text {
      font-size: 13px;
      color: ${COLORS.textMedium || '#666'};
      font-weight: 500;
      flex: 1;
    }

    .ant-pagination-item {
      border-radius: 50% !important;
      border: 1.5px solid ${COLORS.border || '#e8e8e8'};
      min-width: 32px !important;
      height: 32px !important;
      line-height: 28px !important;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;

      a {
        color: ${COLORS.textDark || '#1a1a2e'};
        font-weight: 600;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        font-size: 13px;
      }

      &:hover {
        border-color: ${COLORS.$primaryBlue || '#1890ff'};
        background: rgba(24, 144, 255, 0.1);
        a { color: ${COLORS.$primaryBlue || '#1890ff'}; }
      }

      &.ant-pagination-item-active {
        background: ${COLORS.$primaryBlue || '#1890ff'};
        border-color: ${COLORS.$primaryBlue || '#1890ff'};
        a { color: white; font-weight: 700; }
        &:hover {
          background: ${COLORS.$primaryBlueDark || '#096dd9'};
          border-color: ${COLORS.$primaryBlueDark || '#096dd9'};
          a { color: white; }
        }
      }
    }

    .ant-pagination-prev,
    .ant-pagination-next {
      border-radius: 50% !important;
      border: 1.5px solid ${COLORS.border || '#e8e8e8'};
      min-width: 32px !important;
      height: 32px !important;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;

      &:hover {
        border-color: ${COLORS.$primaryBlue || '#1890ff'};
        background: rgba(24, 144, 255, 0.1);
      }

      &.ant-pagination-disabled {
        border-color: #e8e8e8;
        background: #f5f5f5;
        &:hover { border-color: #e8e8e8; background: #f5f5f5; }
      }
    }

    .ant-pagination-jump-prev,
    .ant-pagination-jump-next {
      border-radius: 50% !important;
      min-width: 32px !important;
      height: 32px !important;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
`;
